import { useAction, useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import styled from 'styled-components'

import { PencilIcon } from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'
import {
  currentCustomThemeAtom,
  currentCustomUiThemeAtom,
  currentThemeIdAtom,
  patchCustomThemeColorsAction,
  setCustomAppearanceAction,
  setCustomAppearanceColorAction,
  setCustomBaseThemeAction,
  ThemeId,
  type Theme,
} from '@root/shared/theme'
import { AnimateHeight, Separator } from '@root/shared/ui-kit'

import {
  CustomThemeForm,
  type BaseThemeOption,
} from './custom-theme/custom-theme-form'
import { ThemePreview } from './theme-preview'

import type { TransKey } from '@root/shared/localization'

type Props = {
  themes: ReadonlyArray<Theme>
}

const ID_TITLE_MAP: Record<ThemeId, TransKey> = {
  [ThemeId.Deluge]: 'theme.deluge.name',
  [ThemeId.DelugeLight]: 'theme.delugeLight.name',
  [ThemeId.Test]: 'theme.test.name',
  [ThemeId.Photgone]: 'theme.photgone.name',
  [ThemeId.Monokai]: 'theme.monokai.name',
}

export const ThemePalette = ({ themes }: Props) => {
  const { t } = useTranslation()
  const [activeThemeId, setActiveThemeId] = useAtom(currentThemeIdAtom)
  const [customTheme] = useAtom(currentCustomThemeAtom)
  const [customUiTheme] = useAtom(currentCustomUiThemeAtom)
  const [isCustomFormOpen, setIsCustomFormOpen] = useState(false)
  const patchColors = useAction(patchCustomThemeColorsAction)
  const setBaseTheme = useAction(setCustomBaseThemeAction)
  const setAppearance = useAction(setCustomAppearanceAction)
  const setAppearanceColor = useAction(setCustomAppearanceColorAction)

  const baseThemeOptions: ReadonlyArray<BaseThemeOption> = themes.map((x) => ({
    id: x.id,
    label: t(ID_TITLE_MAP[x.id]),
  }))

  return (
    <Root>
      <Grid>
        {themes.map((x) => (
          <Item
            key={x.id}
            type="button"
            $isActive={activeThemeId === x.id}
            onClick={() => {
              setActiveThemeId(x.id)
              setIsCustomFormOpen(false)
            }}
          >
            <ItemContent $isActive={x.id === activeThemeId}>
              <ThemeWrapper>
                <ThemePreview theme={x.uiTheme} />
              </ThemeWrapper>
            </ItemContent>
            <Title>{t(ID_TITLE_MAP[x.id])}</Title>
          </Item>
        ))}

        {
          <Item
            as="div"
            $isActive={activeThemeId === 'custom'}
            onClick={() => setActiveThemeId('custom')}
          >
            <ItemContent $isActive={activeThemeId === 'custom'}>
              <ThemeWrapper>
                <ThemePreview dashed theme={customUiTheme} />
              </ThemeWrapper>
            </ItemContent>
            <Title>
              {t('theme.custom.name')}{' '}
              <PencilButton
                type="button"
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  setIsCustomFormOpen((x) => !x)
                }}
              >
                <PencilIcon size={16} />
              </PencilButton>
            </Title>
          </Item>
        }
      </Grid>

      <AnimateHeight>
        {isCustomFormOpen ? (
          <>
            <Separator />
            <CustomThemeFormWrapper
              customTheme={customTheme}
              baseThemeOptions={baseThemeOptions}
              previewTheme={customUiTheme}
              onAppearanceChange={setAppearance}
              onAppearanceColorChange={setAppearanceColor}
              onBaseThemeChange={setBaseTheme}
              onChange={patchColors}
              onSubmit={() => {}}
            />
          </>
        ) : undefined}
      </AnimateHeight>
    </Root>
  )
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
`

const ThemeWrapper = styled.div`
  margin-bottom: -30px;
`

const ItemContent = styled.div<{ $isActive: boolean }>`
  border-radius: 10px;
  outline: 1px solid transparent;
  outline-color: ${(x) => (x.$isActive ? 'var(--primary-9)' : 'none')};

  overflow: hidden;
`

const Item = styled.button<{ $isActive: boolean }>`
  all: unset;
  padding: 0;
  cursor: pointer;
  display: flex;
  flex-direction: column;

  gap: 10px;

  color: ${(x) => (x.$isActive ? 'var(--text-12)' : 'var(--text-10)')};

  &:hover {
    color: ${(x) => (x.$isActive ? 'var(--text-12)' : 'var(--text-11)')};

    > ${ItemContent} {
      outline-color: ${(x) =>
        x.$isActive ? 'var(--primary-9)' : 'var(--primary-6)'};
    }
  }
`

const Title = styled.div`
  text-align: center;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`

const CustomThemeFormWrapper = styled(CustomThemeForm)`
  padding-top: 12px;
`
const PencilButton = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;

  &:hover {
    color: var(--primary-9);
  }
`
